import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
    Box,
    Button,
    Card,
    Container,
    Stack,
    Typography,
    Tab,
    DialogContent,
    Dialog,
    DialogActions,
    DialogTitle,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { TabContext, TabList, TabPanel } from "@mui/lab";
import { toast } from "react-toastify";
import {
    GET_APPROVED_STAKEHOLDERS,
    SEND_TO_SAP_VENDOR,
} from "../../../common/URLs";
import { getLocalStorageItem } from "../../../common/localStorageUtils";
import { loaderService } from "../../../common/custom-loader/loader-service";
import { PROD } from "../../../common/constants";
import BackButton from "../../../common/backButton";
import apiClient from "../../../axios";
import { SendIcon } from "../../../common/icons";
import { QuickSearchToolbar } from "../../../payment/common/commonFunctions";

function SendToSAP() {
    const navigate = useNavigate();
    const location = useLocation();
    const backScrutiny = location?.state?.backScrutiny;

    const [tabValue, setTabValue] = useState("ready");
    const [allRows, setAllRows] = useState([]);
    const [selectionModel, setSelectionModel] = useState([]);
    const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
    const [sendToSAPLoading, setSendToSAPLoading] = useState(false);

    useEffect(() => {
        fetchStakeholders();
    }, []);

    const fetchStakeholders = () => {
        loaderService.showLoader();
        apiClient
            .post(
                GET_APPROVED_STAKEHOLDERS,
                {
                    type: "all",
                },
                {
                    headers: {
                        Authorization: "Bearer " + getLocalStorageItem("id_token"),
                    },
                }
            )
            .then((response) => {
                loaderService.hideLoader();
                const rows = response?.data?.stakeHolders || [];
                setAllRows(rows);
            })
            .catch((error) => {
                loaderService.hideLoader();
                console.log(error);
                toast.error("Failed to fetch stakeholders");
            });
    };

    const readyRows = useMemo(() => {
        return (allRows || []).filter(
            (item) => !item?.sapVendorId || String(item.sapVendorId).trim() === "" || item?.sapUpdated === true
        );
    }, [allRows]);

    const sentRows = useMemo(() => {
        return (allRows || []).filter(
            (item) => item?.sapVendorId && String(item.sapVendorId).trim() !== ""
        );
    }, [allRows]);

    const handleOpenConfirmation = () => {
        if (sendToSAPLoading) return;

        if (!selectionModel || selectionModel.length === 0) {
            toast.warning("Please select at least one row");
            return;
        }

        setOpenConfirmDialog(true);
    };

    const handleCloseConfirmation = () => {
        if (sendToSAPLoading) return;
        setOpenConfirmDialog(false);
    };

    const handleSendToSAP = () => {
        if (sendToSAPLoading) return;

        const payload = {
            stakeHolderMasterIDLst: selectionModel,
        };

        setSendToSAPLoading(true);
        loaderService.showLoader();

        apiClient
            .post(SEND_TO_SAP_VENDOR, payload, {
                headers: {
                    Authorization: "Bearer " + getLocalStorageItem("id_token"),
                },
            })
            .then((response) => {
                loaderService.hideLoader();
                setSendToSAPLoading(false);

                const res = response?.data;

                if (res?.isSuccess) {
                    handleCloseConfirmation();
                    toast.success(res?.message || "Sent to SAP successfully");
                    setSelectionModel([]);
                    fetchStakeholders();
                } else {
                    toast.error(res?.message || "Failed to send to SAP");
                }
            })
            .catch((error) => {
                loaderService.hideLoader();
                setSendToSAPLoading(false);
                console.log(error);
                toast.error(
                    error?.response?.data?.message || "Failed to send to SAP"
                );
            });
    };

    const handleBack = () => {
        if (backScrutiny) {
            navigate("/registrationHome/scrutiny");
        } else {
            navigate(-1);
        }
    };

    const columns = [
        {
            field: "urn",
            headerName: "URN",
            minWidth: 180,
            flex: 1,
        },
        {
            field: "name",
            headerName: "Name",
            minWidth: 220,
            flex: 1.2,
            renderCell: (params) =>
                params.row.subCategory &&
                    params.row.subCategory.toLowerCase() === "individual"
                    ? (params.row.firstName || "") +
                    (params.row.middleName ? " " + params.row.middleName : "") +
                    (params.row.lastName ? " " + params.row.lastName : "")
                    : params.row.name,
        },
        {
            field: "categoryName",
            headerName: "Category",
            minWidth: 180,
            flex: 1,
        },
        {
            field: "mobileNo",
            headerName: "Mobile No",
            minWidth: 150,
            flex: 1,
        },
        {
            field: "state",
            headerName: "State",
            minWidth: 180,
            flex: 1,
        },
        {
            field: "district",
            headerName: "District",
            minWidth: 180,
            flex: 1,
        },
        {
            field: "registrationStatus",
            headerName: "Registration Status",
            minWidth: 180,
            flex: 1,
        },
        {
            field: "vendorStatus",
            flex: 1,
            minWidth: 150,
            headerName: "Vendor Status",
            valueGetter: (params) => {
                const { sapUpdated } = params.row;

                if (!sapUpdated) {
                    return "New Vendor";
                }

                if (sapUpdated) {
                    return "Modified Vendor";
                }

                return "";
            }
        },
        {
            field: "sapVendorId",
            headerName: "SAP Vendor ID",
            minWidth: 180,
            flex: 1,
            hide: tabValue === "ready",
            renderCell: (params) => params.value || "-",
        },
    ];

    return (
        <Container maxWidth="xxl">
            {!PROD && (
                <p style={{ textAlign: "center", fontSize: "2vh" }}>REG-SAP-01</p>
            )}

            <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                mb={2}
                flexWrap="wrap"
                gap={1}
            >
                <Stack direction="row" alignItems="center" spacing={1}>
                    <BackButton navigate={handleBack} />
                    <Typography variant="h5" gutterBottom={false}>
                        Send to SAP
                    </Typography>
                </Stack>

                {tabValue === "ready" && (
                    <Button
                        variant="contained"
                        onClick={handleOpenConfirmation}
                        disabled={!selectionModel?.length || sendToSAPLoading}
                        startIcon={<SendIcon />}
                    >
                        {sendToSAPLoading ? "Sending..." : "Send to SAP"}
                    </Button>
                )}
            </Stack>

            <Card>
                <TabContext value={tabValue}>
                    <Box
                        sx={{
                            borderBottom: 1,
                            borderColor: "divider",
                            bgcolor: "whitesmoke",
                        }}
                    >
                        <TabList
                            onChange={(e, newValue) => {
                                setTabValue(newValue);
                                setSelectionModel([]);
                            }}
                            variant="scrollable"
                            scrollButtons="auto"
                        >
                            <Tab label="Ready to send to SAP" value="ready" />
                            <Tab label="Sent to SAP" value="sent" />
                        </TabList>
                    </Box>

                    <TabPanel value="ready">
                        <Box sx={{ height: 500, width: "100%" }}>
                            <DataGrid
                                rows={readyRows}
                                columns={columns}
                                checkboxSelection
                                disableRowSelectionOnClick
                                pageSizeOptions={[10, 25, 50]}
                                initialState={{
                                    pagination: {
                                        paginationModel: { pageSize: 10, page: 0 },
                                    },
                                }}
                                selectionModel={selectionModel}
                                onSelectionModelChange={(newSelection) =>
                                    setSelectionModel(newSelection)
                                }
                                getRowId={(row) => row.stakeholderMasterId}
                                components={{
                                    Toolbar: QuickSearchToolbar,
                                }}
                            />
                        </Box>
                    </TabPanel>

                    <TabPanel value="sent">
                        <Box sx={{ height: 500, width: "100%" }}>
                            <DataGrid
                                rows={sentRows}
                                columns={columns}
                                disableRowSelectionOnClick
                                pageSizeOptions={[10, 25, 50]}
                                initialState={{
                                    pagination: {
                                        paginationModel: { pageSize: 10, page: 0 },
                                    },
                                }}
                                getRowId={(row) => row.stakeholderMasterId}
                                components={{
                                    Toolbar: QuickSearchToolbar,
                                }}
                            />
                        </Box>
                    </TabPanel>
                </TabContext>
            </Card>

            <Dialog open={openConfirmDialog} onClose={handleCloseConfirmation}>
                <DialogTitle>Confirmation</DialogTitle>
                <DialogContent>
                    Are you sure you want to send {selectionModel.length} selected
                    stakeholder(s) to SAP?
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseConfirmation} disabled={sendToSAPLoading}>
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleSendToSAP}
                        disabled={sendToSAPLoading}
                    >
                        {sendToSAPLoading ? "Sending..." : "Confirm"}
                    </Button>
                </DialogActions>
            </Dialog>
        </Container>
    );
}

export default SendToSAP;